<template>
  <div>
    <my-notification :icon="'square'">
      <template #title>
        {{ $t('compScenarioBoundaries.title') }}
      </template>
      <span v-html="$t('compScenarioBoundaries.info-1')" />
    </my-notification>
    <br />
    <form @submit.prevent="validateBeforeSubmit">
      <b-notification :closable="false">
        <div class="columns">
          <div class="column is-4">
            <b-field
              :label="$t('compScenarioBoundaries.label-1')"
              :type="{ 'is-danger': errors.has('name') }"
              :message="errors.first('name')"
            >
              <b-input
                expanded
                icon="tags"
                name="name"
                :placeholder="$t('compScenarioBoundaries.label-1')"
                v-model="name"
                v-validate="'required|uniqueName'"
              />
            </b-field>
          </div>
          <div class="column is-4">
            <b-field :label="$t('compScenarioBoundaries.label-2')">
              <b-select
                expanded
                icon="thermometer"
                :placeholder="$t('compScenarioBoundaries.label-2')"
                v-model="selectedGHGI"
                @change.native="$emit('is-modified', true)"
              >
                <option
                  v-for="(ghgi, index) in adminStore.allGHGI"
                  :value="ghgi"
                  :key="index"
                >
                  {{ ghgi.year }}
                </option>
              </b-select>
            </b-field>
          </div>
          <div class="column is-4">
            <b-field
              :label="$t('compScenarioBoundaries.label-3')"
              :type="{ 'is-danger': errors.has('timeline') }"
              :message="errors.first('timeline')"
            >
              <b-input
                name="timeline"
                expanded
                v-validate="{
                  required: true,
                  numeric: true,
                  between: [minYear, 2100]
                }"
                icon="crosshairs"
                :placeholder="$t('compScenarioBoundaries.label-3')"
                v-model="timeline"
              />
            </b-field>
          </div>
        </div>
      </b-notification>
      <b-notification :closable="false">
        <b-field :label="$t('compScenarioBoundaries.label-5')">
          <editor v-model="description" />
        </b-field>
      </b-notification>
      <nav v-if="authStore.isAuthenticated" class="level">
        <div class="level-left"></div>
        <div class="level-right">
          <div class="buttons">
            <button
              type="submit"
              class="button is-primary"
              :disabled="!module.isModified"
            >
              <span class="icon is-small"><i class="fa fa-save"></i></span>
              <span> {{ $t('save') }} </span>
            </button>
          </div>
        </div>
      </nav>
    </form>
  </div>
</template>

<script>
import { useAdminStore } from '@/stores/admin.js'
import { useScenarioStore } from '@/stores/scenario.js'
import { useAuthenticationStore } from '@/stores/authentication.js'

const MAX_TIMELINE = 2050

export default {
  name: 'Boundaries',
  props: {
    module: {
      type: Object,
      default: null
    }
  },
  components: {
    editor: () => import('@/components/Editor.vue'),
    'my-notification': () => import('@/components/MyNotification.vue')
  },
  $_veeValidate: {
    validator: 'new'
  },
  data () {
    return {
      selectedGHGI: null,
      changedBug: 0,
      rulesModalActive: false,
      MAX_TIMELINE: MAX_TIMELINE
    }
  },
  setup () {
    const adminStore = useAdminStore()
    const authStore = useAuthenticationStore()
    const scenarioStore = useScenarioStore()
    return { adminStore, authStore, scenarioStore }
  },
  computed: {
    scenario: function () {
      return this.scenarioStore.item
    },
    uuid: function () {
      return this.$route.params.uuid
    },
    minYear: function () {
      return new Date().getFullYear() + 1
    },
    name: {
      get: function () {
        return this.scenario.name
      },
      set: function (name) {
        this.scenarioStore.setName(name)
        this.inputChanged()
      }
    },
    description: {
      get: function () {
        return this.scenario.description
      },
      set: function (description) {
        this.scenarioStore.setDescription(description)
        this.inputChanged()
      }
    },
    timeline: {
      get: function () {
        return this.scenario.timeline
      },
      set: function (timeline) {
        this.scenarioStore.setTimeline(timeline)
        this.inputChanged()
      }
    }
  },
  methods: {
    inputChanged () {
      if (this.changedBug === 1) {
        this.$emit('is-modified', true)
      } else {
        this.changedBug += 1
      }
    },
    validateBeforeSubmit () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          const loadingComponent = this.$buefy.loading.open({
            container: null
          })
          this.scenarioStore
            .save(this.scenario.ghgi.id)
            .then((response) => {
              loadingComponent.close()
              window.scrollTo(0, 0)
              this.$buefy.toast.open({
                type: 'is-light',
                message: this.$t('compScenarioBoundaries.save-msg')
              })
              this.$emit('is-valid', true)
              this.$emit('is-modified', false)
              this.$router
                .push('/scenario-1point5/' + response.uuid)
                .catch(() => {})
            })
            .catch((erreur) => {
              loadingComponent.close()
              this.$emit('is-valid', false)
              this.$emit('is-modified', false)
            })
        }
      })
    }
  },
  watch: {
    selectedGHGI: {
      handler () {
        this.scenarioStore.setGHGI(this.selectedGHGI)
      },
      deep: true
    }
  },
  mounted () {
    if (this.uuid && this.uuid !== '0') {
      this.selectedGHGI = this.adminStore.allGHGI.filter(
        (obj) => obj.id === this.scenario.ghgi.id
      )[0]
    } else if (this.scenario.ghgi) {
      this.selectedGHGI = this.scenario.ghgi
    } else if (this.adminStore.allGHGI.length > 0) {
      this.selectedGHGI = this.adminStore.allGHGI[0]
    }
  },
  created () {
    let self = this
    this.$validator.extend('uniqueName', {
      getMessage: (field) => self.$t('compScenarioBoundaries.error-1'),
      validate: (value) => {
        return (
          self.adminStore.allScenarios.filter(
            (obj) => obj.name === value && obj.uuid !== self.uuid
          ).length === 0
        )
      }
    })
  }
}
</script>
